import * as React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { ListItemText, ListItemIcon, Button, ListItemButton } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Box, Paper, Divider } from '@mui/material';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import { formatToLocalDateTime } from '../../../utils/dateFormatter';
import AttachmentView from '../../common/modals/AttachmentView';
import { downloadFile } from '../../../api/uploadService';
import { triggerDownload } from '../../../utils/triggerDownload';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { useUploadFile } from '../../../hooks/uploads/useUploadFile';
import FileSelectorDialog from '../../common/forms/FileSelectorDialog';
import { useAddAttachmentToHistory } from '../../../hooks/history/useAddAttachmentToHistory';
import { useAddAttachmentToHistoryEntry } from '../../../hooks/history/useAddAttachmentToHistoryEntry';
import { useRemoveAttachmentFromHistory } from '../../../hooks/history/useRemoveAttachmentFromHistory';
import { useRemoveAttachmentFromHistoryEntry } from '../../../hooks/history/useRemoveAttachmentFromHistoryEntry';
import FileRenameForm from '../../common/forms/FileRenameForm';
import { useDeleteFile } from '../../../hooks/uploads/useDeleteFile';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

export default function AttachmentsList({ fetchData, history = null, historyEntry = null, attachments }) {
    const { t } = useTranslation('common');
    const { showSnackbar } = useSnackbar();

    const [viewOpen, setViewOpen] = React.useState(false);
    const [formOpen, setFormOpen] = React.useState(false);

    const [selectedAttachment, setSelectedAttachment] = React.useState(null);

    const [fileSelectorDialogOpen, setFileSelectorDialogOpen] = React.useState(false);
    
    const onFileSelectorClose = () => {
        setFileSelectorDialogOpen(false);
    };

    const [tags, setTags] = React.useState(["medicalHistory"]);

    const onFileUploadSuccess = async (newFile) => {
        try {
          if (history)
          {
            await attach(history.id, { attachmentIds: [newFile.id] });
          } else 
          {
            await attachToEntry(historyEntry.id, { attachmentIds: [newFile.id] });
          }
        } catch (error) {
            showSnackbar(t("file.messages.upload_failed"), "error");
        }
    };
    
    const { upload, progress } = useUploadFile(onFileUploadSuccess);

    const onFileAttachSuccess = () => {
        setFileSelectorDialogOpen(false);
        fetchData();
        showSnackbar(t('files.messages.upload_success'), "success");
    };

    const { attach } = useAddAttachmentToHistory(onFileAttachSuccess);
    const { attachToEntry } = useAddAttachmentToHistoryEntry(onFileAttachSuccess);

    const handleUploadFile = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("tags", JSON.stringify(tags));
        try {
          if (history)
          {
            await upload(history.pet_id, formData);
          }
          else {
            await upload(historyEntry.history.pet.id, formData);
          }
        } catch (error) {
            if (error.status === 415)
            {
                showSnackbar(t("files.messages.unsupported_file_type"), "error");
            }
            else if (error.status === 414)
            {
                showSnackbar(t("files.messages.file_quota_exceeded"), "error");
            }
            else if (error.status === 413)
            {
                showSnackbar(t("files.messages.file_too_large"), "error");
            }
            else {
                showSnackbar(t("files.messages.upload_failed"), "error");
            }
        }
    };

    const onFileDeleteSuccess = () => {
        showSnackbar(t('files.messages.delete_success'), "success");
        fetchData();
    };

    const { remove } = useDeleteFile(onFileDeleteSuccess);

    const onAttachmentRemoveSuccess = async () => {
        try {
            await remove(selectedAttachment.id);
        } catch (err) {
            showSnackbar(t('files.messages.delete_fail'), "error");
        }
    };

    const { removeAttachment } = useRemoveAttachmentFromHistory(onAttachmentRemoveSuccess);
    const { removeAttachmentFromEntry } = useRemoveAttachmentFromHistoryEntry(onAttachmentRemoveSuccess);
    
    const handleDeleteAttachmentClick = async (attachment) => {
        setSelectedAttachment(attachment);
        try {
          if (history)
          {
            await removeAttachment(history.id, selectedAttachment.id);
          }
          else {
            await removeAttachmentFromEntry(historyEntry.id, selectedAttachment.id);
          }
            
        } catch (err) {
            showSnackbar(t('files.messages.delete_fail'), "error");
        }
    };

    const handleListItemClick = (attachment) => {
        setSelectedAttachment(attachment);
        setViewOpen(true);
    };

    const handleDownloadClick = async (attachment) => {
        const fileData = await downloadFile(attachment.id);
        triggerDownload(fileData, attachment.filename);
    };

    const onFileRenameSuccess = () => {
        setFormOpen(false);
        fetchData();
        showSnackbar(t('files.messages.rename_success'), "success");
    };

    const handleRenameClick = (attachment) => {
        setSelectedAttachment(attachment);
        setFormOpen(true);
    };

    return (
        <Paper 
          elevation={3} 
          sx={{ 
            p: 2, 
            borderRadius: 2, 
            // bgcolor: 'background.default' 
          }}
        >
        <Typography variant="h6" >
          Attachments
        </Typography>
        {attachments.length === 0 ? 
          <Divider variant="fullWidth" component="li">
            <Typography variant="caption" color="text.secondary">
              no attachments
            </Typography>
          </Divider>
        : null}
        <List sx={{ mb: 1, width: '100%', bgcolor: 'background.paper' }}>
            {attachments?.map((attachment) => (
            <ListItem 
                key={attachment.id}
                sx={{ py: 0 }}
                alignItems="flex-start"
                secondaryAction={
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <VerticalHamburgerMenu
                        options={[
                            { 
                                id: 1, 
                                name: 'View',
                                callback: () => handleListItemClick(attachment)
                            },
                            { 
                                id: 2, 
                                name: 'Download',
                                callback: () => handleDownloadClick(attachment)
                            },
                            { 
                                id: 3, 
                                name: 'Rename',
                                callback: () => handleRenameClick(attachment)
                            },
                            {
                                id: 4,
                                name: 'Delete',
                                callback: () => handleDeleteAttachmentClick(attachment),
                                confirmMessage: t('files.messages.delete_confirm')
                            }
                        ]}
                        />
                    </Box>
                }
            >
                <ListItemButton sx={{ py: 0 }} onClick={() => handleListItemClick(attachment)}>
                <ListItemIcon>
                    {attachment.mimetype.includes('image/') ? ( 
                        <ImageIcon fontSize="large" color="primary" /> 
                    ) : attachment.mimetype.includes('audio/') ? ( 
                        <AudioFileIcon fontSize="large" color="primary" /> 
                    ) : attachment.mimetype.includes('video/') ? ( 
                        <VideoFileIcon fontSize="large" color="primary" /> 
                    ) : attachment.mimetype.includes('application/pdf') ? ( 
                        <PictureAsPdfIcon fontSize="large" color="primary" /> 
                    ) : ( <InsertDriveFileIcon fontSize="large" color="primary" /> 

                    )}
                </ListItemIcon>
                <ListItemText
                primary={
                    <Typography noWrap={true} variant="body2" sx={{ maxWidth: '100%' }}>
                        {attachment.filename}
                    </Typography>
                }
                secondary={formatToLocalDateTime(attachment.createdAt)}
                />
                </ListItemButton>
            </ListItem>
            ))}
        </List>
        <Button 
            variant='outlined' 
            fullWidth 
            onClick={() => setFileSelectorDialogOpen(true)}
            startIcon={<AttachFileIcon />}
        >Add Attachment</Button>
        <AttachmentView 
            attachment={selectedAttachment}
            open={viewOpen}
            onClose={() => setViewOpen(false)}
        />
        <FileSelectorDialog 
            fileType='any'
            open={fileSelectorDialogOpen}
            onClose={onFileSelectorClose}
            onSubmit={handleUploadFile}
            progress={progress}
        />
        <FileRenameForm 
            successCallback={onFileRenameSuccess}
            attachment={selectedAttachment}
            open={formOpen}
            onClose={() => {
                setFormOpen(false);
            }}
        />
        </Paper>
    );
}